import React, { MouseEvent, Fragment, useEffect, useState } from "react";
import { NavLink } from "react-router";
import PriceDisplay from "../../../../../components/currency";
import { defaultCurrency } from "../../../../../config/currencies";
import LoadingSpinner from "../../../../../components/loading";
import {
  Accordion,
  useAccordionApi,
  //useAccordionState,
  withAccordionProvider
} from "../../../../nbc-ui/accordion";
import { ROUTES } from "../../../../../routes";
import { calculateCost, countTotalItemsInCart } from "../../../../../helpers/cart";
import { getProductUrl } from "../../../../../helpers/product";
import type { Product, ProductVariant } from "../../../../../interfaces/product";
import { useCart, fetchItemDetails, removeItemFromCart
} from "../../../../../store/cart";

export default withAccordionProvider(Minicart);

function Minicart(): React.JSX.Element {
  const cart = useCart();
  const [cartItemsCount, setCartItemsCount] = useState<number>(0);
  const [cartCost, setCartCost] = useState<number>(0);

  const { toggle } = useAccordionApi();
  //const { isExpanded } = useAccordionState();

  useEffect(() => {
    if(cart) {
      setCartCost(calculateCost(cart.items)?.totalSellingPrice ?? 0);
      setCartItemsCount(countTotalItemsInCart(cart));
    }
  }, [cart]);

  function toggleMiniCartDisplay(e: MouseEvent<HTMLDivElement>) {
    e.preventDefault();

    //setShowMiniCart(!showMiniCart);
    toggle();
  }

  return (
    <Fragment>
      <div className="hm-minicart-trigger" onClick={toggleMiniCartDisplay}>
        <span className="item-icon"></span>
        <span className="item-text">
          <PriceDisplay amount={cartCost}
            currencyCode={defaultCurrency.code}
            compact={true}
          />

          { /* If cart is still loading */ !cart &&
            <span className="cart-item-count loading">
              <LoadingSpinner />
            </span>
          }
          { cartItemsCount > 0 &&
            <span className="cart-item-count">
             {cartItemsCount}
            </span>
          }
        </span>
      </div>
      <span></span>
      <Accordion className="minicart" expanded={false}>
        { cartItemsCount === 0 &&
          <p>
            There are no items in your cart yet.
            Add items to your cart to see them reflected here.
          </p>
        }
        { cartItemsCount > 0 &&
          <Fragment>
            <ul className="minicart-product-list">
              { cart?.items?.map(item => (
                <CartItemDetails
                  key={item.id}
                  cartId={cart.id}
                  itemId={item.id}
                  itemType={item.type}
                  itemPrice={item.discountedPrice ?? item.price}
                  quantity={item.quantity}
                />
              ))}
            </ul>
            <p className="minicart-total">
              Subtotal:
              <span>
                <PriceDisplay amount={cartCost}
                  currencyCode={defaultCurrency.code}
                  compact={false}
                />
              </span>
            </p>
            <div className="minicart-button">
              <NavLink to={ROUTES.CART} className="li-button li-button-dark li-button-fullwidth li-button-sm">
                <span>View Full Cart</span>
              </NavLink>
              <NavLink to={ROUTES.CHECKOUT} className="li-button li-button-fullwidth li-button-sm">
                <span>Checkout</span>
              </NavLink>
            </div>
          </Fragment>
        }
      </Accordion>
    </Fragment>
  )
}


interface ICartItemDetails {
  cartId: string,
  itemId: string,
  itemType: string,
  itemPrice: number,
  quantity: number
}

function CartItemDetails(props: ICartItemDetails): React.JSX.Element {
  const { cartId, itemId, itemType, itemPrice, quantity } = props;

  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [itemDetails, setItemDetails] = useState<Product | ProductVariant | null>(null);
  const [itemDetailsUrl, setItemDetailsUrl] = useState<string>("");

  useEffect(() => {
    fetchDetails();

    async function fetchDetails() {
      setIsFetching(true);
      const itemData = await fetchItemDetails({ itemId, itemType });

      if(itemData) {
        setItemDetails(itemData);
        setItemDetailsUrl(getProductUrl({ id: itemData.id}));
      }

      setIsFetching(false);
    }
  }, [itemId, itemType]);

  return (
    <li>
      { isFetching && <span>Fetching...</span> }

      { !isFetching &&
        <Fragment>
          <NavLink
            to={itemDetailsUrl}
            className="minicart-product-image"
          >
            <img src={itemDetails?.featuredImage?.url} alt="cart products" />
          </NavLink>

          <div className="minicart-product-details">
            <h6>
              <NavLink to={itemDetailsUrl}>
                {itemDetails?.name?.substring(0, 15)}
              </NavLink>
            </h6>

            <span>
              <PriceDisplay
                amount={itemPrice}
                currencyCode={defaultCurrency.code}
                compact={false}
              /> x {quantity}
            </span>
          </div>

          <button
            className="close"
            title="Remove from cart"
            onClick={() => removeItemFromCart(cartId, itemId)}
          >
            <i className="fa fa-close"></i>
          </button>
        </Fragment>
      }
    </li>
  );
}
